import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { VisualOverlay } from '../VisualOverlay';
import { CompareView } from './CompareView';
import { ContinueBar } from './ContinueBar';
import { DataTag } from './DataTag';
import { FindingSpotlight } from './FindingSpotlight';
import { InterpretationBlock } from './InterpretationBlock';
import type { Chapter } from '../../types/pulse';
import type { Finding } from '../../data/results';
type Phase = 'promise' | 'spotlight' | 'distribution' | 'interpret' | 'reward';
const ORDER: Phase[] = ['promise', 'spotlight', 'distribution', 'interpret', 'reward'];
interface FindingScreenProps {
  chapter: Chapter;
  finding: Finding;
  total: number;
  selected: string | null;
  isLast: boolean;
  onInterpret: (optionId: string) => void;
  onContinue: () => void;
}
const ease = [0.22, 1, 0.36, 1] as const;
export function FindingScreen({
  chapter,
  finding,
  total,
  selected,
  isLast,
  onInterpret,
  onContinue
}: FindingScreenProps) {
  const [phase, setPhase] = useState<Phase>(selected ? 'reward' : 'promise');
  const scrollRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Partial<Record<Phase, HTMLElement | null>>>({});
  const reached = (p: Phase) => ORDER.indexOf(phase) >= ORDER.indexOf(p);
  useEffect(() => {
    if (phase === 'promise') return;
    const target = phase === 'reward' ? sectionRefs.current.interpret : sectionRefs.current[phase];
    const container = scrollRef.current;
    if (!target || !container) return;
    const id = window.setTimeout(() => {
      const top = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 88;
      container.scrollTo({
        top,
        behavior: 'smooth'
      });
    }, 120);
    return () => window.clearTimeout(id);
  }, [phase]);
  const handleSelect = (id: string) => {
    onInterpret(id);
    setPhase('reward');
  };
  const bar: Record<Phase, {
    label: string;
    onClick: () => void;
    disabled?: boolean;
  }> = {
    promise: {
      label: 'Reveal the answer',
      onClick: () => setPhase('spotlight')
    },
    spotlight: {
      label: 'See all answers',
      onClick: () => setPhase('distribution')
    },
    distribution: {
      label: 'Add your interpretation',
      onClick: () => setPhase('interpret')
    },
    interpret: {
      label: 'Choose an interpretation',
      onClick: () => undefined,
      disabled: true
    },
    reward: {
      label: isLast ? 'What comes next' : 'Next finding',
      onClick: onContinue
    }
  };
  return <div className="relative h-full bg-mist-50" data-id="element-544" data-mp="3ixtr78v9nx7z">
      <div ref={scrollRef} className="relative h-full overflow-y-auto pb-36 no-scrollbar" data-id="element-545" data-mp="3iz4tf492cwwz">
        <figure className="relative h-[250px] w-full overflow-hidden bg-mist-100" data-id="element-546" data-mp="3e620myc41rhu">
          <motion.img src={finding.image} alt="" initial={{
          scale: 1.16
        }} animate={{
          scale: 1.02
        }} transition={{
          duration: 9,
          ease: 'easeOut'
        }} className="absolute inset-0 h-full w-full object-cover" data-id="element-547" data-mp="3a6bdr5ppfvil" />
          <VisualOverlay variant={finding.visual} accent={chapter.accent} data-id="element-548" data-mp="1oel5ol9a33pe" />
        </figure>

        <div className="relative -mt-8 rounded-t-[28px] bg-mist-50 px-6 pt-7" data-id="element-549" data-mp="1tcv02tn6i09y">
          <p className="text-[11px] font-semibold tabular-nums tracking-[0.24em] text-slate-400" data-id="element-550" data-mp="1tezei5ipehoh">
            FINDING <span style={{
            color: chapter.accent
          }} data-id="element-551" data-mp="22dt6tjp04lw8">{finding.number}</span> / 0{total}
          </p>
          <motion.h2 initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.15,
          ease
        }} className="mt-3 font-serif text-[28px] leading-[1.12] text-navy-900" data-id="element-552" data-mp="27cxh6dnnwpo5">
            {finding.hook}
          </motion.h2>

          {reached('spotlight') && <motion.section ref={el => sectionRefs.current.spotlight = el} initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease
        }} className="mt-8" aria-label="Key insight" data-id="element-553" data-mp="2l30ycq7opt6q">
              <FindingSpotlight spotlight={finding.spotlight} options={chapter.options} accent={chapter.accent} data-id="element-554" data-mp="2hdyisyikvll9" />
              <div className="mt-6" data-id="element-555" data-mp="2ceu7a186lbrr">
                <DataTag kind="editorial" data-id="element-556" data-mp="2r5ufc550n3nd" />
                <p className="mt-2 text-[15px] leading-relaxed text-navy-800" data-id="element-557" data-mp="2r4t0rqosjz0w">{finding.observation}</p>
              </div>
            </motion.section>}

          {reached('distribution') && <motion.section ref={el => sectionRefs.current.distribution = el} initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease
        }} className="mt-8 rounded-3xl border border-navy-900/[0.08] bg-white p-5" aria-label="All answers" data-id="element-558" data-mp="1yizwfcnukcc0">
              <p className="mb-3 text-[13px] font-medium text-navy-900" data-id="element-559" data-mp="3jsox2uag7ox5">{chapter.question}</p>
              <CompareView options={chapter.options} accent={chapter.accent} data-id="element-560" data-mp="3eudoemvw3kgi" />
            </motion.section>}

          {reached('interpret') && <motion.section ref={el => sectionRefs.current.interpret = el} initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease
        }} className="mt-10" data-id="element-561" data-mp="3rdhpglvzwcro">
              <InterpretationBlock interpretation={finding.interpretation} accent={chapter.accent} selected={selected} onSelect={handleSelect} data-id="element-562" data-mp="3hheldahl2423" />
            </motion.section>}
        </div>
      </div>

      <ContinueBar label={bar[phase].label} onClick={bar[phase].onClick} disabled={bar[phase].disabled} accent={chapter.accent} data-id="element-563" data-mp="5djpfzxn1uz" />
    </div>;
}